/** Browser automation signals added to client PostHog events (PLE-1968).
 * Strong signals mark `is_suspected_bot`; weak ones are recorded in `bot_signals`
 * only, because some real browsers show them too.
 */

export interface BotSignalInput {
  webdriver: boolean | undefined
  userAgent: string
  screenWidth: number
  screenHeight: number
  viewportWidth: number
  viewportHeight: number
  timeZone: string
  languageCount: number
  standalone: boolean
  /** The browser itself reports full screen (F11 or the Fullscreen API). */
  fullscreen: boolean
}

export interface BotSignalProperties {
  is_suspected_bot: boolean
  bot_signals: string
}

const AUTOMATION_UA = /HeadlessChrome|PhantomJS|Puppeteer|Playwright|Selenium|Chrome-Lighthouse/i
const MOBILE_UA = /iPhone|iPad|iPod|Android/i
const LINUX_DESKTOP_UA = /X11; Linux/
const STRONG_SIGNALS = new Set([
  "webdriver", "automation_ua", "headless_screen", "tz_unknown", "emulated_viewport",
])

function viewportSignal(input: BotSignalInput): string | null {
  const fillsScreen = input.screenWidth > 0 &&
    input.viewportWidth === input.screenWidth && input.viewportHeight === input.screenHeight
  if (!fillsScreen || input.standalone) return null
  // Out of full screen, a real phone or Linux desktop browser loses height to its own
  // toolbars. Macs, Windows and Chromebooks in full screen do not, so there it is only
  // weak, and so is any browser that reports full screen itself (Linux F11 included).
  const emulated = !input.fullscreen &&
    (MOBILE_UA.test(input.userAgent) || LINUX_DESKTOP_UA.test(input.userAgent))
  return emulated ? "emulated_viewport" : "fullscreen_viewport"
}

export function detectBotSignals(input: BotSignalInput): string[] {
  const signals: string[] = []
  if (input.webdriver === true) signals.push("webdriver")
  if (AUTOMATION_UA.test(input.userAgent)) signals.push("automation_ua")
  // Headless Chrome reports an 800x600 screen whatever viewport it was given.
  if (input.screenWidth === 800 && input.screenHeight === 600 && input.viewportWidth > 800) {
    signals.push("headless_screen")
  }
  const viewport = viewportSignal(input)
  if (viewport) signals.push(viewport)
  if (!input.timeZone || input.timeZone === "Etc/Unknown") signals.push("tz_unknown")
  if (input.timeZone === "UTC" || input.timeZone === "Etc/UTC") signals.push("tz_utc")
  if (input.languageCount === 0) signals.push("no_languages")
  return signals
}

export function toBotSignalProperties(signals: string[]): BotSignalProperties {
  return {
    is_suspected_bot: signals.some((signal) => STRONG_SIGNALS.has(signal)),
    bot_signals: signals.join(","),
  }
}

let cached: BotSignalProperties | null = null

function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return iosStandalone || window.matchMedia?.("(display-mode: standalone)").matches === true
}

function isFullscreen(): boolean {
  return Boolean(document.fullscreenElement) ||
    window.matchMedia?.("(display-mode: fullscreen)").matches === true
}

/** Reads the current browser once per page load. Returns null outside a browser. */
export function getBrowserBotSignalProperties(): BotSignalProperties | null {
  if (cached) return cached
  if (typeof window === "undefined" || typeof navigator === "undefined") return null
  try {
    cached = toBotSignalProperties(detectBotSignals({
      webdriver: navigator.webdriver,
      userAgent: navigator.userAgent ?? "",
      screenWidth: window.screen?.width ?? 0,
      screenHeight: window.screen?.height ?? 0,
      viewportWidth: window.innerWidth ?? 0,
      viewportHeight: window.innerHeight ?? 0,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
      languageCount: navigator.languages?.length ?? 0,
      standalone: isStandalone(),
      fullscreen: isFullscreen(),
    }))
    return cached
  } catch {
    // Signal collection must never block analytics.
    return null
  }
}
